import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { type CompareProperty, getCompare } from "../api/client";
import { NavBar } from "../components/NavBar";
import { LoadingState } from "../components/LoadingState";

/**
 * ComparePage — Task 9.5. Side-by-side comparison of 2-5 scored properties.
 * place_ids come from the query string (set by the Saved dashboard's
 * "Compare selected" action). Highlights each property's strongest /
 * weakest factor relative to the set, and flags stale scores.
 */
export function ComparePage() {
  const [params] = useSearchParams();
  const placeIds = (params.get("place_ids") || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const [props, setProps] = useState<CompareProperty[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (placeIds.length < 2) {
      setError("Select at least 2 properties from your saved list to compare.");
      return;
    }
    let cancelled = false;
    getCompare(placeIds)
      .then((r) => {
        if (!cancelled) setProps(r.properties);
      })
      .catch((e: { message?: string; not_found?: string[] }) => {
        if (!cancelled) {
          setError(
            e.not_found
              ? "Some properties haven't been scored yet. Score them first, then compare."
              : e.message || "Could not load comparison.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <div className="app">
      <NavBar />
      <main>
        <header className="page-header">
          <h1>Compare properties</h1>
        </header>

        {error && <p className="error" role="alert">{error}</p>}
        {!props && !error && <LoadingState message="Loading comparison…" />}

        {props && props.length > 0 && (
          <div className="compare-grid" role="table">
            <div className="compare-col compare-labels" role="rowgroup">
              <div className="compare-cell compare-head">&nbsp;</div>
              <div className="compare-cell">Tilt Map</div>
              <div className="compare-cell">Asking</div>
              <div className="compare-cell">Offer range</div>
              <div className="compare-cell">Strongest</div>
              <div className="compare-cell">Weakest</div>
              <div className="compare-cell">Scored</div>
            </div>
            {props.map((p) => (
              <div className="compare-col" role="rowgroup" key={p.place_id}>
                <div className="compare-cell compare-head">{p.address}</div>
                <div className="compare-cell compare-tilt">{p.tilt_map?.quadrant ?? "—"}</div>
                <div className="compare-cell">${p.asking_price.toLocaleString()}</div>
                <div className="compare-cell">
                  ${p.offer_range.low_price.toLocaleString()}–$
                  {p.offer_range.high_price.toLocaleString()}
                </div>
                <div className="compare-cell compare-strong">
                  {labelFor(p.strongest_factor_key, p) ?? "—"}
                </div>
                <div className="compare-cell compare-weak">
                  {labelFor(p.weakest_factor_key, p) ?? "—"}
                </div>
                <div className="compare-cell">
                  {new Date(p.scored_at).toLocaleDateString()}
                  {p.is_stale && (
                    <span className="stale-flag" title="Score older than cache TTL">
                      {" "}· stale
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function labelFor(key: string | null, p: CompareProperty): string | null {
  if (!key) return null;
  const f = p.factors.find((x) => x.factor_key === key);
  return f ? f.name : key;
}
